/**
 * P4 — derive the customer-facing service-fee (card surcharge) note for
 * *documents* (estimate PDF + quote body). Mirrors the backend
 * `getServiceFeeDisclosure` gate so the internal PDF preview and the sent
 * document agree.
 *
 * This is NOT the pre-payment disclosure beside the "Pay by card" button. That
 * one warns about a fee before the customer commits and must stay visible
 * whenever surcharging is on. This note reports a fee that was already charged,
 * so it only appears once a card deposit has actually been collected — on an
 * unpaid or ACH/cash/check estimate no fee was applied and the document must
 * not imply one was.
 *
 * There is no tenant opt-out: a fee the customer paid is a fact of the
 * document, exactly as the receipt states it.
 *
 * Pure + unit-testable so every caller shares one source of truth.
 */
export interface ServiceFeeDisclosure {
  show: boolean;
  percent: number;
  /** What was actually charged, when known. Never recomputed client-side. */
  chargedAmount: number | null;
}

export function deriveServiceFeeDisclosure(
  settings:
    | {
        ccSurchargeEnabled?: boolean;
        surchargePercent?: number;
      }
    | null
    | undefined,
  deposit?: {
    collected?: boolean | null;
    method?: string | null;
    /**
     * The fee recorded on the capture payment. `undefined` means the caller
     * could not read it (a payload without `depositCollectedSummary`), and the
     * note falls back to the tenant's current rate; `null` / `0` means it was
     * read and no fee was charged (ACH-priced, reversed off a debit card, or
     * surcharging was off at pay time) — the document must not claim one.
     */
    chargedAmount?: number | null;
  },
): ServiceFeeDisclosure {
  const active = Boolean(settings?.ccSurchargeEnabled) && (settings?.surchargePercent ?? 0) > 0;
  const percent = active ? (settings?.surchargePercent ?? 0) : 0;
  const cardDepositCollected =
    Boolean(deposit?.collected) &&
    String(deposit?.method ?? '')
      .trim()
      .toLowerCase() === 'card';
  if (!cardDepositCollected) return { show: false, percent, chargedAmount: null };

  if (deposit?.chargedAmount !== undefined) {
    const charged = Number(deposit.chargedAmount ?? 0);
    return charged > 0
      ? { show: true, percent, chargedAmount: charged }
      : { show: false, percent, chargedAmount: null };
  }

  return { show: active, percent, chargedAmount: null };
}
